import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Video, Calendar, Loader2, ExternalLink } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

const HOURLY_INR = 1500;

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function MeetingScheduler({ projectId, token, user, API }) {
  const [open, setOpen] = useState(false);
  const [meetings, setMeetings] = useState([]);
  const [scheduledAt, setScheduledAt] = useState('');
  const [hours, setHours] = useState(1);
  const [agenda, setAgenda] = useState('');
  const [busy, setBusy] = useState(false);

  const fetchMeetings = useCallback(async () => {
    try {
      const res = await fetch(`${API}/projects/${projectId}/meetings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMeetings(data.meetings || []);
      }
    } catch (e) { /* noop */ }
  }, [API, projectId, token]);

  useEffect(() => { fetchMeetings(); }, [fetchMeetings]);

  const book = async () => {
    if (!scheduledAt) { toast.error('Pick a date & time'); return; }
    if (hours < 1 || hours > 8) { toast.error('Duration must be 1–8 hours'); return; }
    setBusy(true);
    try {
      const ok = await loadRazorpayScript();
      if (!ok) { toast.error('Razorpay SDK failed to load'); return; }
      const res = await fetch(`${API}/projects/${projectId}/meeting/create-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ scheduled_at: scheduledAt, duration_hours: Number(hours), agenda })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Booking failed');
        return;
      }
      const data = await res.json();
      const options = {
        key: data.key_id, amount: data.amount, currency: data.currency,
        name: 'CollabHive', description: `Virtual Meeting (${hours}h)`,
        order_id: data.order_id,
        prefill: { name: user?.name || '', email: user?.email || '' },
        theme: { color: '#059669' },
        handler: async function (response) {
          const verify = await fetch(`${API}/projects/${projectId}/meeting/${data.meeting_id}/verify`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify(response)
          });
          if (verify.ok) {
            toast.success('Meeting booked! CollabHive will share the meeting link shortly.');
            setOpen(false);
            setScheduledAt(''); setHours(1); setAgenda('');
            fetchMeetings();
          } else {
            toast.error('Signature verification failed');
          }
        },
      };
      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', () => toast.error('Payment failed'));
      rzp.open();
    } finally {
      setBusy(false);
    }
  };

  const total = hours * HOURLY_INR;

  return (
    <>
      <Card className="border-primary/30">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <Video className="w-4 h-4 text-primary" />
            <h3 className="font-medium">Virtual Meetings</h3>
            <Badge variant="secondary" className="text-[10px] uppercase tracking-wide">Optional</Badge>
          </div>
          {meetings.length === 0 ? (
            <p className="text-sm text-muted-foreground mb-3">
              <b>Optional:</b> if you'd like to discuss the brief or a submission live with your innovator, you can book a virtual meeting. Feel free to skip this — projects don't require any meetings to proceed.
            </p>
          ) : (
            <div className="space-y-2 mb-3">
              {meetings.map((m) => (
                <div key={m.id} className="text-sm border rounded p-2">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-3 h-3 text-muted-foreground" />
                    <span className="font-medium">{new Date(m.scheduled_at).toLocaleString()}</span>
                    <span className="text-xs text-muted-foreground">· {m.duration_hours}h</span>
                  </div>
                  {m.agenda && <p className="text-xs text-muted-foreground mt-1">Agenda: {m.agenda}</p>}
                  {m.meeting_link ? (
                    <a href={m.meeting_link} target="_blank" rel="noreferrer" className="text-xs text-primary mt-1 inline-flex items-center gap-1">
                      Join <ExternalLink className="w-3 h-3" />
                    </a>
                  ) : (
                    <p className="text-xs text-muted-foreground italic mt-1">Link will be shared shortly.</p>
                  )}
                </div>
              ))}
            </div>
          )}
          <Button size="sm" onClick={() => setOpen(true)} className="gap-2" data-testid="schedule-meeting-btn">
            <Video className="w-4 h-4" />
            Schedule a Meeting (₹{HOURLY_INR}/hr)
          </Button>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Schedule a Virtual Meeting</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-muted-foreground">Date & Time</label>
              <Input type="datetime-local" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} data-testid="meeting-datetime" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Duration (hours)</label>
              <Input type="number" min="1" max="8" value={hours} onChange={(e) => setHours(e.target.value)} data-testid="meeting-hours" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Agenda (optional)</label>
              <Textarea value={agenda} onChange={(e) => setAgenda(e.target.value)} rows={3} placeholder="What would you like to discuss?" />
            </div>
            <div className="text-sm border-t pt-3 flex justify-between">
              <span className="text-muted-foreground">Total</span>
              <span className="font-semibold">₹{total.toLocaleString('en-IN')}</span>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={book} disabled={busy} className="gap-2" data-testid="book-meeting-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Video className="w-4 h-4" />}
              Pay & Book
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
